import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomInput,
  DiscardModal,
  InputReadOnly
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Store entities
import { updateCompanyName } from '../../../../../../store/company/company.thunks';

// Styles
import './company-name.scss';

// Models
import { ICompanyProfile } from '../../../../../../interfaces/company.interfaces';

export type TCompanyNameRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TCompanyNameProps = {
  company: ICompanyProfile;
};

export const CompanyName = forwardRef<TCompanyNameRef, TCompanyNameProps>((
    { company }: TCompanyNameProps, ref
  ) => {
    const [form] = Form.useForm<{ company_name: string }>();

    const [discardOpen, setDiscardOpen] = useState<boolean>(false);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [savingChanges, setSavingChanges] = useState<boolean>(false);

    const dispatch = useDispatch<any>();

    const toggleEditMode = (): void => {
      setEditMode(!editMode);
    }

    const toggleDiscardModal = (): void => {
      setDiscardOpen(!discardOpen);
    }

    const onCancelChangesHandler = (): void => {
      if (hasChanges) {
        toggleDiscardModal();
        return;
      }
      toggleEditMode();
    }

    const onDiscardChangesHandler = (state: boolean): void => {
      if (state) {
        form.resetFields();
        setHasChanges(false);
        toggleEditMode();
        toggleDiscardModal();
        return;
      }
      toggleDiscardModal();
    }

    const onValuesChange = (_: { [key: string]: string | string[] }, { company_name }: {
      company_name: string,
    }): void => {
      const isHasChanges = company?.company_name !== company_name;
      setHasChanges(isHasChanges && company_name?.length > 0);
    }

    const saveChanges = async (): Promise<void> => {
      const { company_name } = form.getFieldsValue();
      setSavingChanges(true);
      const { payload } = await dispatch(updateCompanyName(company_name));
      setSavingChanges(false);
      if (!payload) return;
      setEditMode(false);
      setHasChanges(false);
      form.resetFields();
    }

    useImperativeHandle(ref, () => ({
      isHasChanges(): boolean {
        return hasChanges;
      },
      resetState(): void {
        form.setFieldsValue({
          company_name: company?.company_name ?? '',
        });
        setHasChanges(false);
        setEditMode(false);
        setSavingChanges(false);
      }
    }));

    return (
      <div className="company-name">
        <DiscardModal
          open={ discardOpen }
          onCancel={ onDiscardChangesHandler }
          title={ 'Are you sure you want to discard changes?' }
        />
        <div className="company-name--label business--label">
          <div className="label-title">
            Business Name
          </div>
          <div className="label-actions">
            { !editMode &&
              <CustomButton type={ 'text' }
                            text={ company.company_name ? 'Edit' : 'Add' }
                            size={ 'small' }
                            onClick={ toggleEditMode }/>
            }

            { editMode && <>
              { hasChanges && <CustomButton type={ 'primary' }
                                            text={ 'Save' }
                                            size={ 'small' }
                                            loading={ savingChanges }
                                            onClick={ saveChanges }/> }
              <CustomButton type={ 'text' }
                            text={ 'Cancel' }
                            size={ 'small' }
                            disabled={ savingChanges }
                            onClick={ onCancelChangesHandler }/>
            </> }
          </div>
        </div>
        <Form className="company-name--body business--body-items" form={ form } onValuesChange={ onValuesChange }>
          <div className="body-item">
            { (!editMode && company.company_name) &&
              <InputReadOnly label={ 'Business Name' } value={ company?.company_name }/> }

            { editMode &&
              <Form.Item name={ 'company_name' } initialValue={ company.company_name } rules={ [
                { min: 1 }
              ] }>
                <CustomInput type={ 'text' } maxLength={ 62 } placeholder={ 'Business Name' }/>
              </Form.Item> }
          </div>
        </Form>
      </div>
    );
  }
)
